import * as React from 'react';
import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import { GetGroupBy, GetGroupByPlurals, formatNumber, getSuggestedMaxTick, getMaxTickLimit, getLabels } from '../../helpers/ChartHelper';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { Checkbox, createStyles, FormControl, FormControlLabel, FormGroup, IconButton, makeStyles, Theme } from '@material-ui/core';
import { ChartFilter } from '../ChartComponents';
import { SelectedItemProps } from '../MattersByStatusChart';
import { DrillDownType } from '../DrillDownType';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import MTooltip from '@material-ui/core/Tooltip';
import { ChartInfo } from '../../components/ChartInfo';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import { VideoModal } from '../../components/VideoModal';
import { AIChartDialog } from '../../components/Dialogs/AIChartDialog';
import { AIIcon } from '../../components/icons/AIIcon';
import html2canvas from 'html2canvas';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';

import { RV_CHART_CLASS, RV_CHART_COLORS, rvBarDatasetStyle, rvChartAnimation, rvValueAxisStyle, rvCategoryAxisStyle, rvTooltipStyle, isChartDataEmpty } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartLimitNote } from '../ChartLimitNote';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartEmptyState } from '../ChartEmptyState';
import TrendingUpOutlinedIcon from '@material-ui/icons/TrendingUpOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
            },
        },
        titleBarContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
            [`& .${`h1`}`]: {
                display: 'flex',
                alignItems: 'center',
            },
            [`& .${`h2`}`]: {
                fontSize: '2.5rem',
                padding: '10px 0',
            },
            [`& .${`h3`}`]: {
                paddingTop: '10px',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '&:hover': {
                '& .scroll': {
                    display: 'flex !important',
                    '& .Mui-disabled': {
                        '& .MuiSvgIcon-root': {
                            color: 'inherit !important',
                        },
                    },
                },
            },
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        footerDescription: {
            fontSize: '1rem',
            textAlign: 'center',
        },
        scrollUp: {
            display: 'none',
            position: 'absolute',
            top: '30px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        scrollDown: {
            display: 'none',
            position: 'absolute',
            bottom: '90px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)',
        },
        formControl: {
            display: 'flex',
            flexFlow: 'row wrap',
            alignItems: 'center',
            justifyContent: 'center',
        },
        formLabel: {
            marginRight: '10px',
        },
        formGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
    }),
);

export enum MatterOptionEnum {
    CLOSED_MATTER = 0,
    CURRENT_MATTER = 1,
    COMBINED_MATTER = 2,
}

export const getMatterOptionLabel = (matterOption: MatterOptionEnum): string => {
    switch (matterOption) {
        case MatterOptionEnum.CURRENT_MATTER:
            return 'Current Matters';
        case MatterOptionEnum.COMBINED_MATTER:
            return 'Closed & Current Matters';
        default:
            return 'Closed Matters';
    }
};

const getMatterOption = (showClosedMatters: boolean, showCurrentMatters: boolean): MatterOptionEnum => {
    if (showClosedMatters && showCurrentMatters) {
        return MatterOptionEnum.COMBINED_MATTER;
    }
    if (showCurrentMatters) {
        return MatterOptionEnum.CURRENT_MATTER;
    }
    return MatterOptionEnum.CLOSED_MATTER;
};

const getMattersByCategory = (mattersByProfitability: MattersByProfitability, matterOption: MatterOptionEnum): MatterProfitabilityByCategory[] => {
    switch (matterOption) {
        case MatterOptionEnum.CURRENT_MATTER:
            return mattersByProfitability.currentMattersByCategory;
        case MatterOptionEnum.COMBINED_MATTER:
            return mattersByProfitability.combinedMattersByCategory;
        default:
            return mattersByProfitability.closedMattersByCategory;
    }
};

const getSelectedMattersCount = (matterOption: MatterOptionEnum, summary: MattersByProfitabilitySummary): number => {
    switch (matterOption) {
        case MatterOptionEnum.CURRENT_MATTER:
            return summary.currentMattersCount;
        case MatterOptionEnum.COMBINED_MATTER:
            return summary.combinedMattersCount;
        default:
            return summary.closedMattersCount;
    }
};

interface MatterProfitabilityByCategoryChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
    groupBy?: string;
    closeChartKey?: string;
}

interface ChartState {
    showClosedMatters: boolean;
    showCurrentMatters: boolean;
    matterOption: MatterOptionEnum;
    currentMattersCount: number;
    closedMattersCount: number;
    combinedMattersCount: number;
    groupedBy: string[];
    billableHours: number[];
    standardValue: number[];
    profitability: number[];
    counts: number[];
}

const mapCategoryData = (mattersByCategory: MatterProfitabilityByCategory[]) => ({
    groupedBy: mattersByCategory.map((item) => item.groupedBy),
    billableHours: mattersByCategory.map((item) => item.billableHours),
    standardValue: mattersByCategory.map((item) => item.standardValue),
    profitability: mattersByCategory.map((item) => item.profitability),
    counts: mattersByCategory.map((item) => item.count),
});

export const MatterProfitabilityByCategoryChart: React.FC<MatterProfitabilityByCategoryChartProps> = (props) => {
    const classes = useStyles();

    const chartName = 'Matter Profitability';
    const groupBy = props.groupBy ?? 'MatterCategory';
    const closeChartKey = props.closeChartKey ?? 'isMatterProfitabilityByCategoryChartOpen';

    const [isVideoModalOpen, setIsVideoModalOpen] = React.useState<boolean>(false);
    const [isAIDialogOpen, setIsAIDialogOpen] = React.useState<boolean>(false);
    const [base64Data, setBase64Data] = React.useState<string | ArrayBuffer | null>(null);
    const [isBlobGenerating, setIsBlobGenerating] = React.useState<boolean>(false);

    const [currencySymbol, setCurrencySymbol] = React.useState<string>('$');

    const currencyQuery = useCurrencySymbol();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollUpAndPrevRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollDownAndNextRef = React.useRef<any>();

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const [state, setState] = React.useState<ChartState>(() => ({
        showClosedMatters: props.maximizeSelected && typeof props.maximizeSelected.showClosedMatters === 'boolean' ? props.maximizeSelected.showClosedMatters : true,
        showCurrentMatters:
            props.maximizeSelected && typeof props.maximizeSelected.showCurrentMatters === 'boolean' ? props.maximizeSelected.showCurrentMatters : false,
        matterOption:
            props.maximizeSelected && typeof props.maximizeSelected.matterOption === 'number'
                ? (props.maximizeSelected.matterOption as MatterOptionEnum)
                : MatterOptionEnum.CLOSED_MATTER,
        closedMattersCount: 0,
        currentMattersCount: 0,
        combinedMattersCount: 0,
        groupedBy: [],
        billableHours: [],
        standardValue: [],
        profitability: [],
        counts: [],
    }));

    React.useMemo(() => {
        if (!currencyQuery.loading && !currencyQuery.error && currencyQuery.data) {
            setCurrencySymbol(currencyQuery.data.options.currencySymbol.symbol);
        }
        // tslint:disable-next-line: align
    }, [currencyQuery.loading, currencyQuery.data, currencyQuery.error]);

    React.useEffect(() => {
        window.addEventListener('keydown', onKeyDownHandler);

        return () => {
            window.removeEventListener('keydown', onKeyDownHandler);
        };
        // tslint:disable-next-line: align
    }, []);

    // tslint:disable-next-line: no-any
    const onKeyDownHandler = (event: any) => {
        if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey) {
            event.preventDefault();
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            } else {
                event.stopPropagation();
            }
            return;
        }
    };

    const mattersByProfitabilityQuery = useQuery<MattersByProfitabilityData, MattersByProfitabilityParams>(MATTERS_BY_PROFITABILITY_QUERY, {
        variables: {
            groupBy,
            entityGuid: props.chartFilter && props.chartFilter.entityGuid !== undefined ? props.chartFilter.entityGuid : null,
            entityType: props.chartFilter && props.chartFilter.entityType !== undefined ? props.chartFilter.entityType : null,
            responsibility: props.chartFilter && props.chartFilter.responsibility !== undefined ? props.chartFilter.responsibility : null,
            showAllRecords: !!props.showAllRecords,
        },
    });

    React.useEffect(() => {
        if (
            !mattersByProfitabilityQuery.loading &&
            !mattersByProfitabilityQuery.error &&
            mattersByProfitabilityQuery.data &&
            mattersByProfitabilityQuery.data.statistics &&
            mattersByProfitabilityQuery.data.statistics.matter &&
            mattersByProfitabilityQuery.data.statistics.matter.mattersByProfitability
        ) {
            setState((prevState) => {
                const mattersByProfitability = mattersByProfitabilityQuery.data!.statistics.matter.mattersByProfitability;
                const { summary } = mattersByProfitability;
                const matterOption = getMatterOption(prevState.showClosedMatters, prevState.showCurrentMatters);
                const mattersByCategory = getMattersByCategory(mattersByProfitability, matterOption);

                return {
                    ...prevState,
                    matterOption,
                    closedMattersCount: summary.closedMattersCount,
                    currentMattersCount: summary.currentMattersCount,
                    combinedMattersCount: summary.combinedMattersCount,
                    ...mapCategoryData(mattersByCategory),
                };
            });
        }
        // tslint:disable-next-line: align
    }, [mattersByProfitabilityQuery.loading, mattersByProfitabilityQuery.error, mattersByProfitabilityQuery.data]);

    const selectedMattersCount = getSelectedMattersCount(state.matterOption, {
        closedMattersCount: state.closedMattersCount,
        currentMattersCount: state.currentMattersCount,
        combinedMattersCount: state.combinedMattersCount,
    });

    const appendInProgressCriteria = (searchCriterias: SearchCriteria[]) => {
        if (state.matterOption === MatterOptionEnum.CURRENT_MATTER) {
            searchCriterias.push({ property: 'InProgress', operator: 'eq', comparison: '1' });
        } else if (state.matterOption === MatterOptionEnum.CLOSED_MATTER) {
            searchCriterias.push({ property: 'InProgress', operator: 'eq', comparison: '0' });
        }
    };

    const handleTotalClick = () => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            appendInProgressCriteria(searchCriterias);

            props.getSelectedItem(
                searchCriterias,
                state.matterOption !== MatterOptionEnum.CURRENT_MATTER,
                chartName + ` by ${GetGroupBy(groupBy)} - ${getMatterOptionLabel(state.matterOption)}`,
                DrillDownType.matterSummaryDrilldown,
                'Profitability',
            );
        }
    };

    const onCheckboxChange = (name: 'closed' | 'current') => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (
            !mattersByProfitabilityQuery.data ||
            !mattersByProfitabilityQuery.data.statistics ||
            !mattersByProfitabilityQuery.data.statistics.matter ||
            !mattersByProfitabilityQuery.data.statistics.matter.mattersByProfitability
        ) {
            return;
        }

        const checked = event.target.checked;
        let showClosedMatters = name === 'closed' ? checked : state.showClosedMatters;
        let showCurrentMatters = name === 'current' ? checked : state.showCurrentMatters;

        if (!showClosedMatters && !showCurrentMatters) {
            if (name === 'closed') {
                showCurrentMatters = true;
            } else {
                showClosedMatters = true;
            }
        }

        const mattersByProfitability = mattersByProfitabilityQuery.data.statistics.matter.mattersByProfitability;
        const matterOption = getMatterOption(showClosedMatters, showCurrentMatters);
        const mattersByCategory = getMattersByCategory(mattersByProfitability, matterOption);

        setState((prevState) => ({
            ...prevState,
            showClosedMatters,
            showCurrentMatters,
            matterOption,
            ...mapCategoryData(mattersByCategory),
        }));
    };

    const onToggleVideo = () => {
        setIsVideoModalOpen((prev) => !prev);
    };

    // tslint:disable-next-line: no-any
    const onAIGeneratedSuggestion = async (event: any) => {
        setIsBlobGenerating(true);

        event.preventDefault();
        event.stopPropagation();

        if (entireChartRef && entireChartRef.current) {
            const canvas = await html2canvas(entireChartRef.current);
            canvas.toBlob((blob) => {
                if (blob) {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = async () => {
                        const tempBase64data = reader.result;
                        setBase64Data(tempBase64data);
                        setIsAIDialogOpen(true);
                    };
                }
            });
        }

        setIsBlobGenerating(false);
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            return props.onCloseChart(closeChartKey);
        }
    };

    const onScrollUpAndPrev = (type: 'up' | 'prev') => {
        if (chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min > 0) {
            const scrollRatio = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            const min = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            if (scrollRatio < chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min) {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= scrollRatio ? scrollRatio : 1;
            } else {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= min;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= min;
            }
            chartRef.current.options.scales[type === 'up' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }
        scrollDownAndNextRef.current.classList.remove('Mui-disabled');
    };

    const onScrollDownAndNext = (type: 'down' | 'next') => {
        const labels = state.groupedBy;

        if (chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max < labels.length) {
            const remainingBar = labels.length - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max;
            const scrollRatio = chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min;
            if (remainingBar <= scrollRatio) {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += remainingBar;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += remainingBar;
            } else {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += scrollRatio ? scrollRatio : 1;
            }
            chartRef.current.options.scales[type === 'down' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
        scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
    };

    const onScollBar = (type: 'up' | 'down' | 'prev' | 'next') => () => {
        if (type === 'up') {
            onScrollUpAndPrev('up');
        } else if (type === 'down') {
            onScrollDownAndNext('down');
        } else if (type === 'next') {
            onScrollDownAndNext('next');
        } else {
            onScrollUpAndPrev('prev');
        }
    };

    const onDisabledEnabledArrow = (barType: 'horizontal' | 'vertical') => {
        const labels = state.groupedBy;
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].min > 0) {
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }

        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].max < labels.length) {
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    const onZoom = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onMaximize = () => {
        if (props.onMaximizeSelected) {
            props.onMaximizeSelected({
                showClosedMatters: state.showClosedMatters,
                showCurrentMatters: state.showCurrentMatters,
                matterOption: state.matterOption,
            });
        }
    };

    // tslint:disable-next-line: no-any
    const onContextMenu = (event: any) => {
        event.preventDefault();
        if (event.button !== 2) {
            return false;
        }
        return true;
    };

    // tslint:disable-next-line: no-any
    const onDownload =
        (name: string, extension: string = 'jpg') =>
        // tslint:disable-next-line: no-any
        (image: any) => {
            const a = document.createElement('a');
            a.href = image;
            a.download = createFileName(extension, name);
            a.click();
        };

    // tslint:disable-next-line: no-any
    const onDownloadScreenshot = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(`${chartName} Chart`));
            // tslint:disable-next-line: align
        }, 500);
    };

    const chartControlComponent = () => {
        return (
            <>
<IconButtonWrapper aria-label="close">
                    <div className={`${classes.helpIcon} rv-chart-help`}>
                        <ChartInfo
                            description={props.description}
                            fontSize={props.showAllRecords ? 'large' : 'medium'}
                        />
                        {props.infoUrl && (
                            <MTooltip title="Learn more">
                                <IconButton className={classes.learnIcon} aria-label="Learn more" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                    <VideoLibraryOutlinedIcon />
                                </IconButton>
                            </MTooltip>
                        )}
                    </div>
                    {props.isAIFeatureEnabled && (
                        <MTooltip title="AI Assistant">
                            <IconButton
                                disabled={isBlobGenerating}
                                size={props.showAllRecords ? 'medium' : 'small'}
                                onClick={onAIGeneratedSuggestion}
                            >
                                <AIIcon
                                    color="primary"
                                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                                />
                            </IconButton>
                        </MTooltip>
                    )}
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximizeSelected && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    if (mattersByProfitabilityQuery.loading || currencyQuery.loading) {
        return (
            <div className="rv-chart rv-chart-loading">
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    return (
        <>
            {isAIDialogOpen && (
                <AIChartDialog
                    fullWidth={true}
                    maxWidth="md"
                    open={isAIDialogOpen}
                    onClose={() => setIsAIDialogOpen(false)}
                    data={base64Data}
                    query={props.aiPrompt || 'Give me recommendations on this and highlight any exceptions or concerns'}
                    accessSubType={chartName}
                    layoutId={props.layoutId}
                />
            )}
            <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.profitability) ? ' rv-chart--empty' : ''}`} ref={entireChartRef}>
                {isVideoModalOpen && props.infoUrl && (
                    <VideoModal open={isVideoModalOpen} onClose={onToggleVideo} url={props.infoUrl} />
                )}
                {chartControlComponent()}
                <div className={classes.titleBarContainer}>
                    <ChartHeading icon={<TrendingUpOutlinedIcon />} title={<>{chartName} by {GetGroupBy(groupBy)}</>} />
                    <div className={`${classes.total} h2`} onClick={handleTotalClick}>
                        <AnimatedNumber value={selectedMattersCount} format={(amount) => formatNumber(amount)} />%
                    </div>
                    <div className={classes.title}>
                        {getMatterOptionLabel(state.matterOption)}
                    </div>
                </div>
                <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                {props.showAllRecords && (
                    <>
                        <div className={`${classes.scrollUp} scroll`}>
                            <IconButton size="medium" onClick={onScollBar('up')} ref={scrollUpAndPrevRef}>
                                <KeyboardArrowUpIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                        <div className={`${classes.scrollDown} scroll`}>
                            <IconButton size="medium" onClick={onScollBar('down')} ref={scrollDownAndNextRef}>
                                <KeyboardArrowDownIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                    </>
                )}
                <Bar
                    ref={chartRef}
                    onContextMenu={onContextMenu}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.groupedBy[_index];

                            const searchCriterias = new Array<SearchCriteria>();

                            searchCriterias.push({ property: groupBy, operator: 'eq', comparison: item });
                            appendInProgressCriteria(searchCriterias);

                            props.getSelectedItem(
                                searchCriterias,
                                state.matterOption !== MatterOptionEnum.CURRENT_MATTER,
                                `Matter Profitability by ${GetGroupBy(groupBy)} for ${item} - ${getMatterOptionLabel(state.matterOption)}`,
                                DrillDownType.matterSummaryDrilldown,
                                'Profitability',
                            );
                        }
                    }}
                    data={{
                        labels: getLabels(state.groupedBy),
                        datasets: [
                            {
                                label: 'Profitability',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.profitability,
                            },
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                suggestedMax: getSuggestedMaxTick(state.profitability),
                                beginAtZero: true,
                                ticks: {
                                        ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(state.profitability),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                min: 0,
                                max: props.showAllRecords ? 29 : 9,
                                ticks: {
                                        ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                display: false,
                            },
                            title: {
                                display: false,
                                text: `Matter Count - Current: ${formatNumber(state.currentMattersCount)} Closed: ${formatNumber(state.closedMattersCount)}`,
                            },
                            zoom: {
                                pan: {
                                    enabled: props.showAllRecords,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: props.showAllRecords,
                                    },
                                    onZoom: onZoom,
                                },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const index = context.dataIndex;

                                        return [
                                            `Profitability : ${formatNumber(state.profitability[index])}%`,
                                            `Billed Fees : ${currencySymbol}${formatNumber(state.billableHours[index])}`,
                                            `Standard Cost : ${currencySymbol}${formatNumber(state.standardValue[index])}`,
                                            `Matters : ${formatNumber(state.counts[index])}`
                                        ];
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.profitability) && (
                    <ChartEmptyState title="No profitability data" hint={"No matters with profitability to show."} />
                )}
            </div>
            {state.groupedBy.length > 10 && !props.showAllRecords && (
                <ChartLimitNote>The top 10 {GetGroupByPlurals(groupBy)} are shown</ChartLimitNote>
            )}
            <FormControl component="div" className={classes.formControl}>
                <FormGroup className={classes.formGroup}>
                    <FormControlLabel
                        control={<Checkbox checked={state.showClosedMatters} color="primary" name="closed" onChange={onCheckboxChange('closed')} />}
                        label="Closed Matters"
                    />
                    <FormControlLabel
                        control={<Checkbox checked={state.showCurrentMatters} color="primary" name="current" onChange={onCheckboxChange('current')} />}
                        label="Current Matters"
                    />
                </FormGroup>
            </FormControl>
            </div>
        </>
    );
};

export default MatterProfitabilityByCategoryChart;

export interface MattersByProfitabilityParams {
    groupBy: string;
    entityType: number | null;
    entityGuid: string | null;
    responsibility: number | null;
    showAllRecords: boolean;
}

export interface MattersByProfitabilityData {
    statistics: Statistics;
}

export interface Statistics {
    matter: Matter;
}

export interface Matter {
    mattersByProfitability: MattersByProfitability;
}

export interface MattersByProfitability {
    closedMattersByCategory: MatterProfitabilityByCategory[];
    currentMattersByCategory: MatterProfitabilityByCategory[];
    combinedMattersByCategory: MatterProfitabilityByCategory[];
    summary: MattersByProfitabilitySummary;
}

export interface MattersByProfitabilitySummary {
    closedMattersCount: number;
    currentMattersCount: number;
    combinedMattersCount: number;
}

export interface MatterProfitabilityByCategory {
    groupedBy: string;
    billableHours: number;
    standardValue: number;
    profitability: number;
    count: number;
}

const MATTERS_BY_PROFITABILITY_QUERY = gql`
    query MatterStatistics_MattersByProfitability(
        $groupBy: MatterGroupBy!
        $showAllRecords: Boolean!
        $entityType: Int
        $entityGuid: String
        $responsibility: Int
    ) {
        statistics {
            matter {
                mattersByProfitability(
                    groupBy: $groupBy
                    entityType: $entityType
                    entityGuid: $entityGuid
                    responsibility: $responsibility
                    showAllRecords: $showAllRecords
                ) {
                    closedMattersByCategory {
                        groupedBy
                        billableHours
                        standardValue
                        profitability
                        count
                    }
                    currentMattersByCategory {
                        groupedBy
                        billableHours
                        standardValue
                        profitability
                        count
                    }
                    combinedMattersByCategory {
                        groupedBy
                        billableHours
                        standardValue
                        profitability
                        count
                    }
                    summary {
                        closedMattersCount
                        currentMattersCount
                        combinedMattersCount
                    }
                }
            }
        }
    }
`;
